import React, { useState, useEffect } from 'react';
import { Search, BookOpen } from 'lucide-react';
import { useUIStore } from '../../state/uiStore';
import { topicsApi, SearchResult } from '../../api/topics';

export const CommandPalette: React.FC = () => {
  const { commandPaletteOpen, setCommandPaletteOpen } = useUIStore();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
      if (e.key === 'Escape') {
        setCommandPaletteOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [setCommandPaletteOpen]);

  useEffect(() => {
    if (query.length > 2) {
      setLoading(true);
      topicsApi.search(query)
        .then(setResults)
        .finally(() => setLoading(false));
    } else {
      setResults(null);
    }
  }, [query]);

  if (!commandPaletteOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-start justify-center px-4 pt-16 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={() => setCommandPaletteOpen(false)}
        />
        
        <div className="inline-block w-full max-w-md transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:align-middle">
          <div className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Zoek naar onderwerpen, flashcards, vragen..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
              />
            </div>
            
            {loading && (
              <div className="mt-4 text-center text-gray-500">
                Zoeken...
              </div>
            )}
            
            {results && (
              <div className="mt-4 space-y-2">
                {results.topics.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Onderwerpen</h3>
                    {results.topics.map((topic) => (
                      <button
                        key={topic.id}
                        className="w-full text-left p-2 hover:bg-gray-50 rounded flex items-center space-x-2"
                        onClick={() => {
                          window.location.href = `/topics/${topic.slug}`;
                          setCommandPaletteOpen(false);
                        }}
                      >
                        <BookOpen className="h-4 w-4 text-brand-primary" />
                        <span>{topic.title}</span>
                      </button>
                    ))}
                  </div>
                )}
                
                {results.flashcards.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Flashcards</h3>
                    {results.flashcards.slice(0, 3).map((card) => (
                      <div key={card.id} className="p-2 hover:bg-gray-50 rounded">
                        <div className="text-sm font-medium">{card.front}</div>
                        <div className="text-xs text-gray-500">{card.back}</div>
                      </div>
                    ))}
                  </div>
                )}
                
                {results.questions.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Vragen</h3>
                    {results.questions.slice(0, 3).map((question) => (
                      <div key={question.id} className="p-2 hover:bg-gray-50 rounded">
                        <div className="text-sm">{question.question}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
